import { memo, useEffect, useMemo, useRef } from "react";

import usePolledLazyQuery from "apollo/usePolledQuery/usePolledLazyQuery";
import Filters from "components/Filters";
import FiltersContentWrapper from "components/Filters/ContentWrapper";
import { useCachedFilterFields } from "components/Filters/hooks";
import FiltersSidebar from "components/Filters/Sidebar";
import FiltersSplit from "components/Filters/Split";
import { SavedFilterView, SortOption } from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import { AnalyticsPageWebhook } from "hooks/useAnalytics/pages/webhooks";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { SearchQueryPredicate, SearchSuggestionsOutput } from "types/generated";
import { PollingIntervalGroups } from "apollo/constants";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import {
  FILTERS_ORDER_SETTINGS_KEY,
  initialSortDirection,
  initialSortOption,
} from "../constants";
import { SEARCH_WEBHOOKS_SUGGESTIONS } from "../gql";
import { sortItemOrder, webhooksSuggestionsDictionary, EXCLUDED_FILTER_FIELDS } from "./constants";

type WebhooksFiltersLayoutProps = {
  predicates: SearchQueryPredicate[];
  children: React.ReactNode;
  currentSavedView?: SavedFilterView;
  setCurrentSavedView: (view?: SavedFilterView) => unknown;
};

const WebhooksFiltersLayout = ({
  predicates,
  children,
  currentSavedView,
  setCurrentSavedView,
}: WebhooksFiltersLayoutProps) => {
  const fieldsRef = useRef<string[] | null>(null);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const { onError } = useTypedContext(FlashContext);

  const [
    loadSearchNamedWebhooksIntegrationsSuggestions,
    { data: filteringData, loading, refetch },
  ] = usePolledLazyQuery<{
    searchNamedWebhooksIntegrationsSuggestions: SearchSuggestionsOutput;
  }>(SEARCH_WEBHOOKS_SUGGESTIONS, { pollingGroup: PollingIntervalGroups.Lists });

  const cachedFiltersData = useCachedFilterFields(
    filteringData?.searchNamedWebhooksIntegrationsSuggestions?.fields
  );

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter((field) => field.orderable && field.name !== "slug")
        .map((field) => {
          return {
            value: field.name,
            label: webhooksSuggestionsDictionary[field.name] || field.name,
          };
        })
        .sort((a, b) => sortItemOrder.indexOf(a.value) - sortItemOrder.indexOf(b.value)) || []
    );
  }, [cachedFiltersData]);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: webhooksSuggestionsDictionary,
    excludedFilterFields: EXCLUDED_FILTER_FIELDS,
  });

  // Unmount/navigation mid-request aborts the pending query - not a real failure,
  // so don't surface it to the user.
  const reportIfNotAborted = (error: Error) => {
    if (error?.name !== "AbortError") {
      onError(error);
    }
  };

  const loadSearchSuggestions = (initial = false) => {
    loadSearchNamedWebhooksIntegrationsSuggestions({
      variables: {
        input: {
          fullTextSearch: searchInput,
          predicates,
          fields: initial ? null : fieldsRef.current,
        },
      },
    }).catch(reportIfNotAborted);
  };

  const handlePollingActiveSections = (fields: string[]) => {
    fieldsRef.current = fields;

    refetch({
      input: {
        fullTextSearch: searchInput,
        predicates,
        fields,
      },
    }).catch(reportIfNotAborted);
  };

  useEffect(() => {
    loadSearchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predicates, searchInput]);

  // initial request with nullish fields
  useEffect(() => {
    loadSearchSuggestions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Filters
      filters={filters}
      filtersItemsOptionsMap={filterOptionsMap}
      filtersLoading={loading}
      sortOptions={sortOptions}
      initialSortOption={initialSortOption}
      initialSortDirection={initialSortDirection}
      pollActiveSections={handlePollingActiveSections}
      filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
      filtersDictionary={webhooksSuggestionsDictionary}
      filtersType="webhooks"
      currentSavedView={currentSavedView}
      setCurrentSavedView={setCurrentSavedView}
    >
      <FiltersSplit>
        <FiltersSidebar analyticsPage={AnalyticsPageWebhook.WebhooksList} />

        <FiltersContentWrapper>{children}</FiltersContentWrapper>
      </FiltersSplit>
    </Filters>
  );
};

export default memo(WebhooksFiltersLayout);
